import type { PersonResult, PersonSelection } from '../types';

// Filas del buscador de personas: qué se muestra, a qué grupo va, si se puede elegir y
// por qué no. Lógica pura (sin React) para mantener el componente chico y testeable.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface PickerItem {
  key: string;
  group: 'qms' | 'lms' | null;
  label: string;
  email: string | null;
  note: string | null;
  invite: boolean;
  /** null = no se puede elegir (la nota explica por qué). */
  selection: PersonSelection | null;
}

function fromPerson(p: PersonResult, allowExternal: boolean, allowLms: boolean): PickerItem {
  if (p.source === 'qms') {
    const inactive = p.status === 'inactive';
    const blockedExternal = p.external && !allowExternal;
    return {
      key: `qms-${p.userId}`,
      group: 'qms',
      label: p.name,
      email: p.email,
      note: inactive
        ? 'Desactivada: reactívala en Usuarios'
        : blockedExternal
          ? 'Proveedor externo: no puede ser responsable'
          : p.external
            ? 'Proveedor externo'
            : null,
      invite: false,
      selection:
        inactive || blockedExternal
          ? null
          : { kind: 'qms', userId: p.userId, name: p.name, email: p.email },
    };
  }
  return {
    key: `lms-${p.authSubject ?? 'sin-cuenta'}-${p.email}`,
    group: 'lms',
    label: p.name,
    email: p.email,
    note:
      p.authSubject === null
        ? 'Aún no activa su cuenta del LMS'
        : allowLms
          ? null
          : 'Agrégala primero en Usuarios',
    invite: false,
    selection:
      p.authSubject === null || !allowLms
        ? null
        : { kind: 'lms', authSubject: p.authSubject, name: p.name, email: p.email },
  };
}

/** Personas en orden de grupo (QMS, LMS) y, al final, la opción de invitar si aplica. */
export function buildItems(
  results: PersonResult[],
  q: string,
  allowInvite: boolean,
  allowExternal = true,
  /** Personas del LMS elegibles (agregarlas al QMS exige gestión de roles). */
  allowLms = true,
): PickerItem[] {
  const items = [
    ...results.filter((r) => r.source === 'qms').map((p) => fromPerson(p, allowExternal, allowLms)),
    ...results.filter((r) => r.source === 'lms').map((p) => fromPerson(p, allowExternal, allowLms)),
  ];
  const email = q.toLowerCase();
  const canInvite =
    allowInvite && EMAIL_RE.test(email) && !results.some((r) => r.email.toLowerCase() === email);
  if (canInvite) {
    items.push({
      key: 'invite',
      group: null,
      label: `Invitar a ${email} como proveedor externo`,
      email: null,
      note: null,
      invite: true,
      selection: { kind: 'invite', email },
    });
  }
  return items;
}
